import type { AppReleaseSnapshotPayload } from '@streamlain/permission-snapshot-contract';
import {
  checkAppReleaseEnvelope,
  type LocalAuthorityEnvelope,
  type LocalAuthorityVerifyStatus,
} from '@streamlain/view-config';

import { signedLocalAuthorityKeys } from './signed-local-authority';

export type AppReleaseCheckResult = LocalAuthorityVerifyStatus;

/** Who runs which App on which server: the scope a confirmation must name. */
export interface AppReleaseExpectation {
  userId: string;
  workspaceId: string;
  appBaseId: string;
  serverId: string;
  serverBaseUrlHash: string;
  tenantId?: string | null;
}

/**
 * Checks the signed release confirmation of an App before a published App is
 * embedded: the shared checklist with the scope of this person, workspace,
 * App and server, and the digest of the manifest that is about to run.
 */
export async function checkAppReleaseConfirmation(
  envelope: LocalAuthorityEnvelope<AppReleaseSnapshotPayload>,
  expected: AppReleaseExpectation,
  manifest: unknown,
  now: Date
): Promise<AppReleaseCheckResult> {
  const check = await checkAppReleaseEnvelope(envelope, {
    ...signedLocalAuthorityKeys('permission-grant'),
    now,
    manifest,
    accepts: (payload, grant) =>
      payload.workspaceId === expected.workspaceId &&
      payload.serverId === expected.serverId &&
      payload.serverBaseUrlHash === expected.serverBaseUrlHash &&
      (!expected.tenantId || payload.tenantId === expected.tenantId) &&
      payload.principal.type === 'workspace-member' &&
      payload.principal.userId === expected.userId &&
      grant.appBaseId === expected.appBaseId,
  });
  return check.status;
}
